import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  GitBranch,
  ChevronRight,
  Search,
  Filter,
  Clock,
  CheckCircle2,
  XCircle,
  PlayCircle,
  Calendar,
  AlertCircle,
} from "lucide-react";
import { releaseCyclesApi, groupsApi, ReleaseCycleStatus } from "../api/client";

const statusConfig: Record<
  ReleaseCycleStatus,
  { label: string; icon: React.ReactNode; className: string }
> = {
  planning: {
    label: "Planning",
    icon: <Clock className="w-4 h-4" />,
    className: "bg-gray-100 text-gray-700",
  },
  in_progress: {
    label: "In Progress",
    icon: <PlayCircle className="w-4 h-4" />,
    className: "bg-blue-100 text-blue-700",
  },
  completed: {
    label: "Completed",
    icon: <CheckCircle2 className="w-4 h-4" />,
    className: "bg-green-100 text-green-700",
  },
  cancelled: {
    label: "Cancelled",
    icon: <XCircle className="w-4 h-4" />,
    className: "bg-red-100 text-red-700",
  },
};

function ReleaseCycleStatusBadge({ status }: { status: ReleaseCycleStatus }) {
  const config = statusConfig[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.className}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

export default function ReleaseCycles() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);

  const groupFilter = searchParams.get("group") || "";
  const statusFilter = searchParams.get("status") || "";

  const { data: releaseCyclesData, isLoading: releaseCyclesLoading } = useQuery(
    {
      queryKey: ["release-cycles", groupFilter, statusFilter],
      queryFn: () =>
        releaseCyclesApi.list({
          group_id: groupFilter || undefined,
          status: (statusFilter as ReleaseCycleStatus) || undefined,
        }),
    },
  );

  const { data: groupsData } = useQuery({
    queryKey: ["groups"],
    queryFn: () => groupsApi.list(),
  });

  const releaseCycles = releaseCyclesData?.release_cycles || [];
  const groups = groupsData?.groups || [];

  // Status priority for sorting: in_progress first, then planning, then completed/cancelled
  const statusPriority: Record<ReleaseCycleStatus, number> = {
    in_progress: 0,
    planning: 1,
    completed: 2,
    cancelled: 3,
  };

  const filteredReleaseCycles = releaseCycles
    .filter((cycle) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        cycle.title.toLowerCase().includes(query) ||
        cycle.customer_version.toLowerCase().includes(query) ||
        cycle.group_name?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      // First sort by status priority
      const statusDiff = statusPriority[a.status] - statusPriority[b.status];
      if (statusDiff !== 0) return statusDiff;

      // Within same status, sort by target date (soonest first, null dates last)
      const dateA = a.target_prod_date
        ? new Date(a.target_prod_date).getTime()
        : Infinity;
      const dateB = b.target_prod_date
        ? new Date(b.target_prod_date).getTime()
        : Infinity;
      return dateA - dateB;
    });

  const handleGroupChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set("group", value);
    } else {
      params.delete("group");
    }
    setSearchParams(params);
  };

  const handleStatusChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set("status", value);
    } else {
      params.delete("status");
    }
    setSearchParams(params);
  };

  const formatTargetDate = (dateStr?: string) => {
    if (!dateStr) return null;
    // Parse as local date to avoid timezone shift
    // Date-only strings like "2026-01-17" are interpreted as UTC by default
    const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Release Cycles</h1>
          <p className="text-gray-600 mt-1">
            Coordinate multi-service releases with customer versions and
            deployment tracking
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Release Cycle
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search release cycles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
          </div>

          {/* Group Filter */}
          <div className="w-48">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={groupFilter}
                onChange={(e) => handleGroupChange(e.target.value)}
                className="input pl-10 w-full appearance-none"
              >
                <option value="">All Groups</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Status Filter */}
          <div className="w-40">
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="input w-full"
            >
              <option value="">All Statuses</option>
              <option value="planning">Planning</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Release Cycle List */}
      {releaseCyclesLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : filteredReleaseCycles.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No release cycles found
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || groupFilter || statusFilter
              ? "Try adjusting your filters"
              : "Get started by creating your first release cycle"}
          </p>
          {!searchQuery && !groupFilter && !statusFilter && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Release Cycle
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Release Cycle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deployments
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredReleaseCycles.map((cycle) => (
                <tr
                  key={cycle.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/release-cycles/${cycle.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <GitBranch className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {cycle.title}
                        </div>
                        {cycle.description && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {cycle.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {cycle.group_name || "-"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-brand-100 text-brand-800">
                      v{cycle.customer_version}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {cycle.target_prod_date ? (
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-1.5 text-gray-400" />
                        {formatTargetDate(cycle.target_prod_date)}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ReleaseCycleStatusBadge status={cycle.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cycle.deployment_count ?? 0} deployments
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/release-cycles/${cycle.id}`);
                      }}
                      className="text-brand-600 hover:text-brand-700"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateReleaseCycleModal
          groups={groups}
          onClose={() => setShowCreateModal(false)}
          onCreated={(id) => {
            setShowCreateModal(false);
            navigate(`/release-cycles/${id}`);
          }}
        />
      )}
    </div>
  );
}

interface CreateReleaseCycleModalProps {
  groups: { id: string; name: string }[];
  onClose: () => void;
  onCreated: (id: string) => void;
}

function CreateReleaseCycleModal({
  groups,
  onClose,
  onCreated,
}: CreateReleaseCycleModalProps) {
  const [formData, setFormData] = useState({
    group_id: "",
    customer_version: "",
    title: "",
    description: "",
    target_prod_date: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await releaseCyclesApi.create({
        group_id: formData.group_id || undefined,
        customer_version: formData.customer_version,
        title: formData.title,
        description: formData.description || undefined,
        target_prod_date: formData.target_prod_date || undefined,
      });
      onCreated(result.id);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create release cycle",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Create Release Cycle
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group
              </label>
              <select
                value={formData.group_id}
                onChange={(e) =>
                  setFormData({ ...formData, group_id: e.target.value })
                }
                className="input w-full"
              >
                <option value="">Select a group (optional)</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Version <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.customer_version}
                onChange={(e) => {
                  let value = e.target.value;
                  // Strip leading 'v' or 'V' to prevent "vv" display
                  if (value.toLowerCase().startsWith("v")) {
                    value = value.slice(1);
                  }
                  setFormData({ ...formData, customer_version: value });
                }}
                placeholder="e.g., 1.11, 2.0"
                className="input w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="e.g., Q1 2025 Release"
                className="input w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Optional description..."
                rows={3}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Production Date
              </label>
              <input
                type="date"
                value={formData.target_prod_date}
                onChange={(e) =>
                  setFormData({ ...formData, target_prod_date: e.target.value })
                }
                className="input w-full"
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Create Release Cycle"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
